import React, { useState, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Switch,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { HiChevronDown, HiSearch, HiChevronDoubleUp, HiChevronDoubleDown } from "react-icons/hi";
import {
  isSpecialPrivilege,
  extractEntities,
  groupEntitiesByCategory,
  filterEntities,
  getInitialState,
  getStateFromExistingPrivileges,
  getSpecialPrivilegesFromExisting,
} from "@/utils/privilegeEditor";
import type { PrivilegeLevel } from "@/utils/privilegeEditor";
import type { TierChange } from "@/api/hooks/admin.hooks";

export interface RoleTierEditorHandle {
  getTierChanges: () => TierChange[];
  getSpecialPrivileges: () => string[];
}

interface RoleTierEditorProps {
  allPrivileges: Array<{ id: string; name: string }>;
  initialPrivilegeNames?: string[];
}

const LEVEL_LABELS: Record<PrivilegeLevel, string> = {
  none: "Ninguno",
  read: "Lectura",
  update: "Actualizar",
  manage: "Administrar",
};

const RoleTierEditor = forwardRef<RoleTierEditorHandle, RoleTierEditorProps>(
  ({ allPrivileges, initialPrivilegeNames = [] }, ref) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [allExpanded, setAllExpanded] = useState(true);

    const privilegeNames = useMemo(() => allPrivileges.map((p) => p.name), [allPrivileges]);

    const privilegeNameToId = useMemo(() => {
      const map = new Map<string, string>();
      for (const p of allPrivileges) {
        map.set(p.name, p.id);
      }
      return map;
    }, [allPrivileges]);

    const entities = useMemo(() => extractEntities(privilegeNames), [privilegeNames]);

    const initialPrivKey = initialPrivilegeNames.slice().sort().join(",");

    const initialEntityStates = useMemo(() => {
      if (initialPrivilegeNames.length > 0) {
        return getStateFromExistingPrivileges(privilegeNames, initialPrivilegeNames);
      }
      return getInitialState(privilegeNames);
    }, [privilegeNames, initialPrivKey]);

    const initialSpecialPrivs = useMemo(
      () => getSpecialPrivilegesFromExisting(initialPrivilegeNames),
      [initialPrivKey]
    );

    const [entityStates, setEntityStates] = useState<Map<string, PrivilegeLevel>>(
      () => new Map(initialEntityStates)
    );
    const [specialPrivs, setSpecialPrivs] = useState<Set<string>>(() => new Set(initialSpecialPrivs));

    const initialStatesRef = useRef(initialEntityStates);
    const initialSpecialRef = useRef(initialSpecialPrivs);

    useEffect(() => {
      initialStatesRef.current = initialEntityStates;
      initialSpecialRef.current = initialSpecialPrivs;
      setEntityStates(new Map(initialEntityStates));
      setSpecialPrivs(new Set(initialSpecialPrivs));
    }, [initialPrivKey]);

    const specialPrivilegeItems = useMemo(
      () => allPrivileges.filter((p) => isSpecialPrivilege(p.name)),
      [allPrivileges]
    );

    const groupedEntities = useMemo(() => groupEntitiesByCategory(entities), [entities]);

    const filteredEntities = useMemo(
      () => filterEntities(entities, searchTerm),
      [entities, searchTerm]
    );

    const filteredGroups = useMemo(() => {
      if (!searchTerm.trim()) return groupedEntities;
      const filtered = new Map<string, string[]>();
      const lowerSearch = searchTerm.toLowerCase();

      for (const [category, ents] of groupedEntities) {
        const matching = ents.filter(
          (e) => e.toLowerCase().includes(lowerSearch) || category.toLowerCase().includes(lowerSearch)
        );
        if (matching.length > 0) {
          filtered.set(category, matching);
        }
      }
      return filtered;
    }, [groupedEntities, searchTerm]);

    useImperativeHandle(ref, () => ({
      getTierChanges: () => {
        const changes: TierChange[] = [];
        for (const [entity, currentLevel] of entityStates) {
          const initialLevel = initialStatesRef.current.get(entity) || "none";
          if (currentLevel !== initialLevel) {
            changes.push({ entity, level: currentLevel });
          }
        }
        return changes;
      },
      getSpecialPrivileges: () => {
        const added: string[] = [];
        for (const privName of specialPrivs) {
          if (!initialSpecialRef.current.has(privName)) {
            added.push(privName);
          }
        }
        const removed: string[] = [];
        for (const privName of initialSpecialRef.current) {
          if (!specialPrivs.has(privName)) {
            removed.push(privName);
          }
        }
        return [...added, ...removed.map((n) => `__REMOVE__${n}`)];
      },
    }));

    const handleLevelChange = useCallback((entity: string, level: PrivilegeLevel) => {
      setEntityStates((prev) => {
        const next = new Map(prev);
        next.set(entity, level);
        return next;
      });
    }, []);

    const handleGroupLevelChange = useCallback((groupEntities: string[], level: PrivilegeLevel) => {
      setEntityStates((prev) => {
        const next = new Map(prev);
        for (const entity of groupEntities) {
          next.set(entity, level);
        }
        return next;
      });
    }, []);

    const getGroupLevel = useCallback(
      (groupEntities: string[]): PrivilegeLevel | "mixed" => {
        const levels = groupEntities
          .map((e) => entityStates.get(e) || "none")
          .filter((v, i, arr) => arr.indexOf(v) === i);
        return levels.length === 1 ? levels[0] : "mixed";
      },
      [entityStates]
    );

    const handleSpecialToggle = useCallback((privName: string, checked: boolean) => {
      setSpecialPrivs((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(privName);
        } else {
          next.delete(privName);
        }
        return next;
      });
    }, []);

    const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
      const all = new Set<string>();
      for (const [category] of groupedEntities) {
        all.add(category);
      }
      return all;
    });

    const handleExpandAll = () => {
      const all = new Set<string>();
      for (const [category] of groupedEntities) {
        all.add(category);
      }
      setExpandedSections(all);
      setAllExpanded(true);
    };

    const handleCollapseAll = () => {
      setExpandedSections(new Set());
      setAllExpanded(false);
    };

    const handleToggleSection = (category: string) => {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        if (next.has(category)) {
          next.delete(category);
        } else {
          next.add(category);
        }
        return next;
      });
    };

    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            placeholder="Buscar entidades..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <HiSearch size={16} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={allExpanded ? "Colapsar todos" : "Expandir todos"}>
            <IconButton size="small" onClick={allExpanded ? handleCollapseAll : handleExpandAll}>
              {allExpanded ? <HiChevronDoubleUp size={16} /> : <HiChevronDoubleDown size={16} />}
            </IconButton>
          </Tooltip>
        </Box>

        {specialPrivilegeItems.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Privilegios Especiales
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {specialPrivilegeItems.map((sp) => (
                <Box
                  key={sp.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 0.5,
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip label={sp.name} size="small" variant="outlined" color="warning" />
                    {(sp as any).description && (
                      <Typography variant="caption" color="text.secondary">
                        {(sp as any).description}
                      </Typography>
                    )}
                  </Box>
                  <Switch
                    size="small"
                    checked={specialPrivs.has(sp.name)}
                    onChange={(e) => handleSpecialToggle(sp.name, e.target.checked)}
                    color="warning"
                    inputProps={{ "aria-label": `Toggle ${sp.name}` }}
                  />
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: 1.5 }} />
          </Box>
        )}

        <Box sx={{ maxHeight: 350, overflowY: "auto" }}>
          {Array.from(filteredGroups.entries()).map(([category, categoryEntities]) => (
            <Accordion
              key={category}
              expanded={expandedSections.has(category) || searchTerm.trim().length > 0}
              onChange={() => handleToggleSection(category)}
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "4px !important",
                mb: 0.5,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<HiChevronDown size={16} />}
                sx={{ minHeight: 36, "& .MuiAccordionSummary-content": { my: 0.5 } }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {category}
                </Typography>
                <Chip
                  label={categoryEntities.length}
                  size="small"
                  sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 0.5,
                    px: 0.5,
                    mb: 0.5,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Aplicar a todos:
                  </Typography>
                  <RadioGroup
                    row
                    value={getGroupLevel(categoryEntities)}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val !== "mixed") {
                        handleGroupLevelChange(categoryEntities, val as PrivilegeLevel);
                      }
                    }}
                    aria-label={`Nivel para todos en ${category}`}
                    name={`group-privilege-${category}`}
                    sx={{ gap: 0.5 }}
                  >
                    {(Object.keys(LEVEL_LABELS) as PrivilegeLevel[]).map((level) => (
                      <FormControlLabel
                        key={level}
                        value={level}
                        control={
                          <Radio
                            size="small"
                            color={
                              level === "manage"
                                ? "error"
                                : level === "update"
                                  ? "warning"
                                  : level === "read"
                                    ? "info"
                                    : "default"
                            }
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                            {LEVEL_LABELS[level]}
                          </Typography>
                        }
                        sx={{ mr: 0.5, "& .MuiTypography-root": { fontSize: "0.7rem" } }}
                      />
                    ))}
                  </RadioGroup>
                </Box>
                {categoryEntities.map((entity) => {
                  const currentLevel = entityStates.get(entity) || "none";
                  const initialLevel = initialStatesRef.current.get(entity) || "none";
                  const isDirty = currentLevel !== initialLevel;
                  return (
                    <Box
                      key={entity}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 0.25,
                        borderBottom: "1px solid",
                        borderColor: isDirty ? "warning.light" : "action.hover",
                        bgcolor: isDirty ? "warning.fade" : "transparent",
                      }}
                    >
                      <Typography variant="body2" fontWeight={500} sx={{ minWidth: 100 }}>
                        {entity}
                        {isDirty && (
                          <Chip label="*" size="small" sx={{ ml: 0.5, height: 16, fontSize: "0.6rem" }} />
                        )}
                      </Typography>
                      <RadioGroup
                        row
                        value={currentLevel}
                        onChange={(e) => handleLevelChange(entity, e.target.value as PrivilegeLevel)}
                        aria-label={`Privilegios para ${entity}`}
                        name={`privilege-${entity}`}
                        sx={{ gap: 0.5 }}
                      >
                        {(Object.keys(LEVEL_LABELS) as PrivilegeLevel[]).map((level) => (
                          <FormControlLabel
                            key={level}
                            value={level}
                            control={
                              <Radio
                                size="small"
                                color={
                                  level === "manage"
                                    ? "error"
                                    : level === "update"
                                      ? "warning"
                                      : level === "read"
                                        ? "info"
                                        : "default"
                                }
                              />
                            }
                            label={
                              <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                                {LEVEL_LABELS[level]}
                              </Typography>
                            }
                            sx={{ mr: 0.5, "& .MuiTypography-root": { fontSize: "0.7rem" } }}
                          />
                        ))}
                      </RadioGroup>
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          ))}

          {filteredGroups.size === 0 && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No se encontraron entidades
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

RoleTierEditor.displayName = "RoleTierEditor";

export default RoleTierEditor;
